<script setup lang="ts">
import { ref } from 'vue'
import type { StatementMoment, StatementRecord } from '../types'
import { sourceById } from '../content'
import { formatDate } from '../lib/format'

const props = defineProps<{ statement: StatementRecord }>()
const loaded = ref<Record<string, boolean>>({})
const shareStatus = ref('')
const label: Record<string, string> = {
  'contradicao-direta': 'Contradição direta',
  'versao-modificada': 'Versão modificada',
  'afirmacao-incompleta': 'Afirmação incompleta',
  'questao-ainda-aberta': 'Questão ainda aberta',
  'alegacao-nao-confirmada': 'Alegação não confirmada',
  'mudanca-de-versao': 'Mudança de versão',
  'choque-com-os-documentos': 'Choque com os documentos',
  'contexto-contestado': 'Contexto contestado',
}
const load = (moment: StatementMoment) => { if (moment.videoId) loaded.value[moment.videoId] = true }
const source = (moment: StatementMoment) => sourceById.get(moment.sourceId)
const share = async () => {
  const url = `${window.location.origin}/antes-depois#${props.statement.slug}`
  try {
    if (navigator.share) await navigator.share({ title: props.statement.shortTitle, text: props.statement.whatChanged, url })
    else { await navigator.clipboard.writeText(url); shareStatus.value = 'Link copiado.' }
  } catch { shareStatus.value = 'Não foi possível compartilhar.' }
}
</script>

<template>
  <article :id="statement.slug" class="before-after">
    <header>
      <div><span class="eyebrow">Rastreio Político · comparação documental</span><h2>{{ statement.title }}</h2></div>
      <div class="classification-list"><span v-for="item in statement.classifications" :key="item">{{ label[item] }}</span></div>
    </header>
    <div class="moments">
      <section v-for="moment in [statement.before, statement.after]" :key="`${statement.id}-${moment.label}`" class="moment">
        <div class="moment-heading"><strong>{{ moment.label }}</strong><time :datetime="moment.date">{{ formatDate(moment.date) }}</time></div>
        <div v-if="moment.mediaType === 'video' && moment.videoId" class="video-shell">
          <iframe v-if="loaded[moment.videoId]" :src="`https://www.youtube-nocookie.com/embed/${moment.videoId}?start=${moment.timestamp ?? 0}`" :title="`${moment.label}: ${statement.title}`" loading="lazy" allow="accelerometer; encrypted-media; picture-in-picture" allowfullscreen />
          <button v-else type="button" @click="load(moment)"><span aria-hidden="true">▶</span> Carregar vídeo da fonte</button>
        </div>
        <a v-else-if="moment.mediaType === 'video' && moment.videoUrl" class="document-shell media-link" :href="moment.videoUrl" target="_blank" rel="noopener noreferrer"><span>Vídeo original</span><strong>Abrir na fonte</strong></a>
        <div v-else class="document-shell"><span>{{ moment.mediaType === 'document' ? 'Documento' : 'Reportagem' }}</span><strong>Fonte original aberta</strong></div>
        <blockquote v-if="moment.verifiedAsDirectQuote">“{{ moment.transcript }}”</blockquote>
        <p v-else class="moment-summary">{{ moment.transcript }}</p>
        <p>{{ moment.context }}</p>
        <a v-if="source(moment)" :href="source(moment)?.url" target="_blank" rel="noopener noreferrer">{{ source(moment)?.publisher }} · abrir fonte original</a>
      </section>
    </div>
    <footer>
      <div><strong>O que mudou?</strong><p>{{ statement.whatChanged }}</p><strong>Resposta apresentada</strong><p>{{ statement.response }}</p></div>
      <div class="share-tools"><button type="button" @click="share">Compartilhar comparação</button><a :href="`#${statement.slug}`" aria-label="Link direto para esta comparação">Link direto</a><small v-if="shareStatus" role="status">{{ shareStatus }}</small></div>
    </footer>
  </article>
</template>
